/** One panel of a record view (plan §1.1, §2.4; DESIGN.md §5): a `SectionCard` holding
 *  its section's Editable fields, then a hairline and the muted "From <restech>" vendor
 *  block, then the System block of derived / provenance values. That split IS the
 *  read-only distinction — no lock icon on every field. A section without editable fields
 *  (a read-only tab of the tabs view, an order's Bookings panel) opens on its first
 *  block, without the hairline.
 *
 *  The same DOM in view and edit mode: FieldSlots swap their control part only, so
 *  entering edit mode re-lays nothing out. The panel is its section's anchor
 *  (`anchorId`) — the spine nodes and the number keys scroll to it and focus it.
 *
 *  The panel owns the shell, the blocks and their groups; the record kind renders each
 *  group's fields (`renderFields` — its field grid of FieldSlots, with its displays). */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { SectionCard } from '@/components/ReadView';

import type { ViewBlock, ViewFieldRef, ViewSection } from '../views/types';

import { BlockRule } from './BlockRule';

/** A titled sub-group of a block (the guest vs this booking; an order's identity). */
export interface RecordFieldGroup {
  id: string;
  title?: ReactNode;
  fields: ViewFieldRef[];
  /** The record kind's grid variant for the group (an order's `identity` grid). */
  layout?: string;
}

export interface RecordPanelProps {
  section: ViewSection;
  /** The panel element's id — what the spine's nodes scroll to. */
  anchorId: string;
  /** One group's fields as the record kind renders them. */
  renderFields: (group: RecordFieldGroup) => ReactNode;
  /** Right side of the panel header ("times in Europe/London"). */
  meta?: ReactNode;
  /** Sub-groups of the editable block. Default: one group. */
  groupEditable?: ((fields: ViewFieldRef[]) => RecordFieldGroup[]) | undefined;
  /** Sub-groups of the vendor block. Default: one group. */
  groupVendor?: ((fields: ViewFieldRef[]) => RecordFieldGroup[]) | undefined;
  /** The vendor block's note, after its label (default: owned by the booking system); a
   *  block's own `note` wins. */
  vendorNote?: string;
  /** The system block's heading. */
  systemTitle?: { title: string; note?: string };
  /** After the editable fields (a booking's party rows). */
  afterEditable?: ReactNode;
  /** After the vendor fields (a booking's tickets grid). */
  afterVendor?: ReactNode;
  /** After the system fields (related records, links). */
  afterSystem?: ReactNode;
}

const SYSTEM_TITLE = { title: 'System', note: 'derived and provenance' };
const VENDOR_NOTE = 'read-only, owned by the booking system';

export function RecordPanel({
  section,
  anchorId,
  renderFields,
  meta,
  groupEditable,
  groupVendor,
  vendorNote = VENDOR_NOTE,
  systemTitle = SYSTEM_TITLE,
  afterEditable,
  afterVendor,
  afterSystem,
}: RecordPanelProps) {
  const block = (kind: ViewBlock['kind']) => section.blocks.find((b) => b.kind === kind);
  const editable = block('editable');
  const vendor = block('vendor');
  const system = block('system');
  const all = (fields: ViewFieldRef[]): RecordFieldGroup[] => [{ id: 'all', fields }];

  return (
    <Box
      component="section"
      id={anchorId}
      tabIndex={-1}
      aria-label={section.title}
      data-stage-panel={section.id}
      sx={{
        // Clears the sticky spine when a node scrolls the panel into view.
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard title={section.title} action={meta}>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {editable && (
            <Groups groups={(groupEditable ?? all)(editable.fields)} renderFields={renderFields} />
          )}
          {afterEditable}
          {(vendor || afterVendor) && (
            <BlockRule
              icon={<SyncIcon aria-hidden sx={{ fontSize: 14 }} />}
              title={vendor?.label ?? 'From the booking system'}
              note={vendor?.note ?? vendorNote}
              rule={Boolean(editable || afterEditable)}
            >
              {vendor && <Groups groups={(groupVendor ?? all)(vendor.fields)} renderFields={renderFields} />}
              {afterVendor}
            </BlockRule>
          )}
          {/* A block with no catalog field still holds what the record kind adds to it
              (a person's sign-in facts in System). */}
          {(system || afterSystem) && (
            <BlockRule
              title={systemTitle.title}
              {...(systemTitle.note ? { note: systemTitle.note } : {})}
              rule={Boolean(editable || afterEditable || vendor || afterVendor)}
            >
              {system && <Groups groups={all(system.fields)} renderFields={renderFields} />}
              {afterSystem}
            </BlockRule>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
}

function Groups({
  groups,
  renderFields,
}: {
  groups: RecordFieldGroup[];
  renderFields: (group: RecordFieldGroup) => ReactNode;
}) {
  return (
    <>
      {groups
        .filter((g) => g.fields.length > 0)
        .map((g) => (
          <Box key={g.id} sx={{ display: 'grid', gap: 0.5 }} data-field-group={g.id}>
            {g.title && (
              <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
                {g.title}
              </Typography>
            )}
            {renderFields(g)}
          </Box>
        ))}
    </>
  );
}
